import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { BillingFeature, MetricType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import setField from 'webapp/helpers/set-field';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomMetricsEditActionsCustomQueryComponent from 'webapp/components/custom-metrics/edit/actions/custom-query';
import CustomMetricsEditActionsHorizonComponent from 'webapp/components/custom-metrics/edit/actions/horizon';
import CustomQuerySetupApiMetricBuilderGeneralSettingsComponent from 'webapp/components/custom-query/setup/api-metric-builder/general-settings';
import CustomQuerySetupApiMetricBuilderRawResponseAndExpressionComponent from 'webapp/components/custom-query/setup/api-metric-builder/raw-response-and-expression';
import CustomQuerySetupCommonDimensionSelectorComponent from 'webapp/components/custom-query/setup/common/dimension-selector/index';
import CustomQuerySetupCommonPreviewChartComponent from 'webapp/components/custom-query/setup/common/preview-chart';
import CustomQuerySetupCommonPreviewTableComponent from 'webapp/components/custom-query/setup/common/preview-table';
import CustomQuerySetupCommonSourceSelectComponent from 'webapp/components/custom-query/setup/common/source-select';
import CustomQuerySetupDatasetsDatasetsComponent from 'webapp/components/custom-query/setup/datasets/datasets';
import CustomQuerySetupHorizonComponent from 'webapp/components/custom-query/setup/horizon/horizon';
import CustomQuerySetupPrebuiltItemComponent from 'webapp/components/custom-query/setup/prebuilt/item';
import CustomQuerySetupSheetDataComponent from 'webapp/components/custom-query/setup/sheet/data';
import CustomQuerySetupSheetLoaderComponent from 'webapp/components/custom-query/setup/sheet/loader';
import FormErrorComponent from 'webapp/components/form-error';
import FormInputComponent from 'webapp/components/form/input';
import MetricLibraryPopupsMetricBasicInfoTooltipComponent from 'webapp/components/metric-library/popups/metric-basic-info-tooltip';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type CustomMetricSetupFormDataset from 'webapp/models-custom/custom-metric-setup-form/dataset';
import CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type CustomQuery from 'webapp/models-custom/custom-query';
import MetricFromClient from 'webapp/models-custom/metric-from-client';

import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

export type CustomMetricSetupForm =
	| CustomMetricSetupFormPrebuilt
	| CustomMetricSetupFormSheet
	| CustomMetricSetupFormApiMetricBuilder;

export type OnFormCloseFn = (query?: CustomQuery, isNew?: boolean, isDeleted?: boolean) => void;

interface Args {
	model: CustomQuery | null; // can be null while still loading
	origin: string;
	inPopup?: boolean;
	onFormClose: OnFormCloseFn;
	onFormReInit?: (form: CustomMetricSetupForm) => void;
	toggleWizardFlow?: () => void;
	preloadedForm?: CustomMetricSetupForm; // could be passed when switching from wizard
	fromWizard?: boolean; // in-page-only
	cloneQuery?: (query: CustomQuery) => void; // in-popup only
	controlIconsNode?: HTMLElement;
	openMetricDefinitionPopupOnInit?: boolean;
	viewOnly?: boolean;
}

interface CustomQuerySetupCommonFormComponentInterface {
	Args: Args;
	Blocks: { default: [] };
}

export default class CustomQuerySetupCommonFormComponent extends Component<CustomQuerySetupCommonFormComponentInterface> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('account') declare accountService: Service.Account;

	@tracked isCustomQueriesLoading = true;
	@tracked submitInProgress = false;
	@tracked hasUserEditedQueryName = false;
	@tracked isFormPreloaded = false;

	@tracked form!: CustomMetricSetupForm;

	@tracked editedName = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void this.mmCustomQueriesService.requestCustomQueries().then(() => {
			this.isCustomQueriesLoading = false;
		});

		this.setupForm(this.args.preloadedForm);

		// In case if wizard is possible we trigger same event in possible-wizard.ts
		const source = this.args.model?.sourceObj;
		if (this.args.model?.isNew && !this.args.model?.isWizardPossible && source) {
			this.analyticsService.event(TrackingEvent.QUERY_SOURCE_SELECTED, {
				datasource: source.sourceType.key,
			});
		}

		this.updateEditedNameFromName();
	}

	get isViewOnly(): boolean {
		return !!this.args.viewOnly;
	}

	get isFormDisabled(): boolean {
		return this.isViewOnly || this.submitInProgress;
	}

	get modelNotLoading() {
		return this.args.model!;
	}

	get prebuiltForm() {
		return this.form as CustomMetricSetupFormPrebuilt;
	}

	get sheetsForm() {
		return this.form as CustomMetricSetupFormSheet;
	}

	get customApiMetricBuilderForm() {
		return this.form as CustomMetricSetupFormApiMetricBuilder;
	}

	get datasetForm() {
		const datasetForm = this.form as CustomMetricSetupFormDataset;
		const sourceObj = this.args.model?.sourceObj;

		if (sourceObj && datasetForm) {
			datasetForm.sourceId = +sourceObj.id!;
			datasetForm.sourceTypeId = sourceObj.sourceType.id;
			datasetForm.selectedSourceOption = sourceObj.selectedSourceOption;
			datasetForm.isTypeIngest = sourceObj.isTypeIngest;
		}

		return datasetForm;
	}

	get isLoading(): boolean {
		return this.form?.isLoadingBuilder || this.isCustomQueriesLoading;
	}

	get isFormVisible(): boolean {
		return !this.isLoading && !!this.args.model?.sourceObj;
	}

	get isEdit(): boolean {
		return !!this.args.model && !this.args.model.isNew;
	}

	get isCloning(): boolean {
		return !!this.args.model?.isNew && !!this.args.model?.cloneFrom;
	}

	get showPreviewChart(): boolean {
		return !!this.form?.isFormReady;
	}

	get showBottomPart(): boolean {
		return !this.isLoading && !!this.form;
	}

	get saveQueryBtnTooltipText(): string {
		let tooltipText = '';
		if (this.form?.isSaveButtonDisabled) {
			tooltipText = 'Run preview first before saving this built metric';
		}
		return tooltipText;
	}

	get isSourceFromDisabled(): boolean {
		return this.submitInProgress || this.isEdit || this.isViewOnly;
	}

	get modelSourceId(): number {
		return this.args.model!.sourceId;
	}

	get shouldShowHorizonBuilder(): boolean {
		const source = this.args.model?.sourceObj;
		if (!source) {
			return false;
		}
		if (source.isTypeOfDataset) {
			return false;
		}

		// The backend strips 'horizon' from availableMetricOptions for spaces not listed in the
		// integration's horizonEnabledForSpaceIds, so this is the per-space rollout gate.
		if (!source.sourceType.isHorizonSupported) {
			return false;
		}

		// TODO: remove once all legacy metrics are migrated to horizon
		const metric = this.args.model?.metric;
		if (metric && !metric.isTypeHorizon) {
			return false;
		}

		return true;
	}

	get shouldShowDatasetBuilder(): boolean {
		const source = this.args.model?.sourceObj;
		if (!source) return false;
		return !!source.isTypeOfDataset;
	}

	get shouldShowLegacyBuilder(): boolean {
		const source = this.args.model?.sourceObj;
		if (!source) return false;
		return !this.shouldShowHorizonBuilder && !this.shouldShowDatasetBuilder;
	}

	get isDatasetOrMergedDataset(): boolean {
		return this.form.query.isDataset || this.form.query.isMergedDataset;
	}

	get datasetHelperText(): string {
		let helperText = '';
		if (this.datasetForm?.selected) {
			Object.entries(this.datasetForm.selected).map(([key, value]) => {
				const item = this.prebuiltForm.items.find((item) => item.id === key);

				const items = item as typeof item & { rawOptions?: SomeDataObject[] };
				const option = items?.rawOptions?.find((option) => option.id === value);

				if (option?.selectedHelperText) {
					helperText = option?.selectedHelperText;
				}
			});
		}

		return helperText;
	}

	@cached
	get temporaryMetric() {
		const query = this.form.query;

		if (query.isApiMetricBuilder) {
			return null;
		}

		const form = this.form as CustomMetricSetupFormPrebuilt | CustomMetricSetupFormSheet,
			metricDefinitionField = form.items.find((i) => {
				return i instanceof CustomMetricSetupFormItemField && i.isMetricDefinition;
			}) as CustomMetricSetupFormItemField | undefined,
			description = (
				metricDefinitionField?.value as
					APIData.CustomQuery.MetricDefinitionValue | null | undefined
			)?.description,
			source = query.sourceObj;

		if (!query || !source || !description) {
			return null;
		}

		// reusing MetricFromClient bcos it does not require ED records
		return new MetricFromClient({
			id: 'temporary',
			spaceAccessId: query.sourceId,
			key: 'temporary',
			name: this.editedName,
			description,
			dataSourceTypeKey: source.sourceType.key,
			objectType: MetricType.CUSTOM_QUERY,
			sourceAvatar: query.sourceAvatar!.avatar,
			sourceName: query.sourceAvatar!.name || '',
			dimensions: [],
			isStandalone: true,
			hasUnits: false,
		});
	}

	@action
	triggerFormLoaded(node: HTMLElement): void {
		if (this.isLoading) {
			return;
		}

		if (this.form?.isFormReady) {
			schedule('afterRender', () => {
				if (node) {
					this.form?.onFormLoaded(node);
				}
			});
		}
	}

	@action
	setupForm(form?: CustomMetricSetupForm) {
		if (!this.args.model) {
			return;
		}

		// Horizon builder handles its own form — skip legacy setup.
		if (this.shouldShowHorizonBuilder) {
			return;
		}

		if (!form) {
			this.args.model.builderId = String(this.args.model.sourceId);

			// create new form and load data for it only if it's not given (preloadedForm)
			form = this.mmCustomQueriesService.createSetupFormForCustomQuery(this.args.model);
			next(() => {
				// when changing sources and we have possible-wizard flow, must not initBuilder twice
				if (isAlive(this) && form) {
					void form.initBuilder();
				}
			});
			if (this.args.onFormReInit) {
				this.args.onFormReInit(form);
			}
		}

		this.form = form;
		this.isFormPreloaded = !!this.args.preloadedForm && this.args.preloadedForm === form;

		form.openMetricDefinitionPopupOnInit = this.args.openMetricDefinitionPopupOnInit ?? false;

		form.onError = this.mmCustomQueriesService.showErrorInPopup.bind(
			this.mmCustomQueriesService,
		);
		form.externalFieldUpdate = this.externalFieldUpdate.bind(this);
		form.trackingOrigin = this.args.origin;
	}

	@action
	changeSource(sourceId: number): void {
		const model = this.args.model;
		if (model) {
			model.sourceId = sourceId;
			model.sourceToId = sourceId;
		}
		this.setupForm();
	}

	@action
	onQueryNameFocusIn(): void {
		this.form?.handleItemAction('queryName', 'focus-in');
	}

	@action
	updateEditedNameFromName(): void {
		this.editedName = this.modelNotLoading.queryName;
	}

	@action
	onQueryNameFocusOut(): void {
		const name = this.editedName.trim();
		if (name && name !== this.modelNotLoading.queryName) {
			this.changeQueryName(name);
		}
	}

	@action
	changeQueryName(name: string): void {
		const model = this.args.model;
		if (model) {
			model.queryName = name;
			this.form.previewBlock.name = name;
		}
		this.hasUserEditedQueryName = true;
	}

	@action
	saveQuery(): void {
		const form = this.form,
			query = this.args.model;

		if (!form || !query) {
			return;
		}

		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		let validatePromise = Promise.resolve(true);
		if (query.isDirty) {
			validatePromise = this.validate();
		}

		validatePromise
			.then((isValid) => {
				this.submitInProgress = false;
				if (isValid) {
					this.mmCustomQueriesService.checkMigrateData(query, form).then(
						() => {
							const isNew = query.isNew;

							this.submitInProgress = true;

							this.mmCustomQueriesService
								.saveCustomQuery(query, form, this.args.origin)
								.then(
									() => {
										next(() => {
											if (isAlive(this)) {
												this.cleanupForm();
												this.closeForm(query, isNew);
											}
										});
									},
									() => {
										// do nothing, error is shown already
									},
								)
								.finally(() => {
									if (isAlive(this)) {
										this.submitInProgress = false;
									}
								});
						},
						() => {
							// do nothing, stay on the form
						},
					);
				}
			})
			.catch(() => {
				/* probably form closed already - ignore */
			});
	}

	@action
	cloneQuery(): void {
		const query = this.args.model;

		if (!query) {
			return;
		}

		if (this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS, true)) {
			if (this.args.cloneQuery) {
				this.args.cloneQuery(query);
			} else {
				this.mmCustomQueriesService.cloneQuery(
					query,
					this.args.origin as TrackingOriginForMetricEdit,
				);
			}
		}
	}

	@action
	cancel() {
		if (this.isEdit) {
			this.args.model?.model.rollbackAttributes();
		}
		this.cleanupForm();
		this.closeForm();
	}

	@action
	deleteQuery(): void {
		const model = this.args.model;

		if (!model) {
			return;
		}

		const onConfirm = () => {
			if (this.args.inPopup) {
				this.closeForm(undefined, undefined, true);
			}

			const layer = this.infolayerService.loading('Deleting built metric...');

			model.delete().then(
				() => {
					layer.ok('Built metric deleted!');

					if (!this.args.inPopup) {
						this.navigationService.transitionToRoute('metrics');
					}
				},
				() => {
					layer.close();
				},
			);
		};

		this.popupService.openConfirmPopup({
			onConfirm,
			title: `Are you sure you want to delete the built metric "${model.queryName}"?`,
			text: '',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	triggerPreviewButtonAction(): void {
		const form = this.form;
		if (!form) {
			return;
		}
		void this.validate().then((isValid) => {
			if (isValid) {
				form.triggerGetPreview();
			}
		});
	}

	@action
	onContentChange(): void {
		if (this.args.inPopup) {
			scheduleOnce('afterRender', this, this.resizePopup);
		}
		this.form?.onContentChange();
	}

	@action
	setupView(node: HTMLElement): void {
		if (this.isFormPreloaded && this.args.model?.isNew) {
			this.triggerFormLoaded(node);
		}
	}

	@action
	teardownView(): void {
		this.cleanupForm();
	}

	private validate(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			const isModelValid = !!this.args.model?.validate();

			// trigger validating on fields
			const form = this.form;

			if (form) {
				void form.validate().then(() => {
					resolve(isModelValid && form.isValid);
				});
			} else {
				reject();
			}
		});
	}

	private resizePopup(): void {
		this.mmCustomQueriesService.repositionEditPopup();
	}

	private externalFieldUpdate(fieldInfo: SomeDataObject): void {
		// could be called on query field /validate response if extra fields are returned
		// f.ex. on gsheets when validation value range returns suggestion for "format" field
		Object.keys(fieldInfo).forEach((itemId) => {
			if (itemId === 'queryName') {
				// only update if user has not edited it yet and its not being duplicated
				if (
					!this.isEdit &&
					!this.hasUserEditedQueryName &&
					this.args.model &&
					!this.args.model.cloneFrom
				) {
					this.args.model.queryName = fieldInfo[itemId];
				}
			} else {
				this.form?.updateExternalField(itemId, fieldInfo[itemId]);
			}
		});
	}

	@action
	closeForm(query?: CustomQuery, isNew = false, isDeleted = false): void {
		next(() => {
			this.args.onFormClose(query, isNew, isDeleted);
		});
	}

	private cleanupForm(): void {
		if (!this.isFormPreloaded) {
			if (this.form) {
				this.form.onFormUnload();
			}
		}
	}
	<template>
		{{#if this.shouldShowHorizonBuilder}}

			<div class="horizon-metric-builder-flow">
				<div
					class="form form--custom-query theme-dark tw:dark form--visible horizon-form-header"
				>
					<div class="form__top-bar">
						<CustomQuerySetupCommonSourceSelectComponent
							@sourceId={{@model.sourceId}}
							@onChange={{this.changeSource}}
							@isDisabled={{this.isSourceFromDisabled}}
							@isCloning={{this.isCloning}}
							@fromWizard={{@fromWizard}}
						/>
						<CustomMetricsEditActionsHorizonComponent
							@isNew={{true}}
							@sourceTypeKey={{@model.sourceObj.sourceType.key}}
							@duplicate={{this.cloneQuery}}
							@delete={{this.deleteQuery}}
						/>
					</div>
				</div>

				<CustomQuerySetupHorizonComponent
					@sourceId={{this.modelSourceId}}
					@onSave={{this.closeForm}}
					@onCancel={{this.closeForm}}
				/>
			</div>

		{{else}}

			<div
				class={{joinClasses
					(array
						"form"
						"form--custom-query"
						"theme-dark"
						"tw:dark"
						(if this.isLoading "form--loading")
						(if this.isFormVisible "form--visible")
					)
				}}
				{{didInsert this.setupView}}
				{{didUpdate (fn this.setupForm undefined) @model}}
				{{didUpdate this.triggerFormLoaded this.isLoading}}
				{{didUpdate
					this.onContentChange
					this.isLoading
					this.form.isLoadingPreview
					this.prebuiltForm.builderType
					this.form.previewDataForBottomTable
					this.form.errors
				}}
				{{willDestroy this.teardownView}}
			>

				<div class="form__top">

					<div class="form__setup">

						<CustomQuerySetupCommonSourceSelectComponent
							@sourceId={{@model.sourceId}}
							@onChange={{this.changeSource}}
							@isDisabled={{this.isSourceFromDisabled}}
							@isCloning={{this.isCloning}}
							@fromWizard={{@fromWizard}}
						/>

						{{#if this.isLoading}}
							<BarLoaderComponent />
						{{/if}}

						<div class="row items ct-settings">
							{{#if @model.isApiMetricBuilder}}
								{{#if this.isFormVisible}}
									<CustomQuerySetupApiMetricBuilderGeneralSettingsComponent
										@form={{this.customApiMetricBuilderForm}}
										@isDisabled={{this.isFormDisabled}}
										@onContentChange={{this.onContentChange}}
										@groupAdvanced={{true}}
										@theme="dark"
									/>
									<CustomQuerySetupApiMetricBuilderRawResponseAndExpressionComponent
										@form={{this.customApiMetricBuilderForm}}
										@isDisabled={{this.isFormDisabled}}
										@onContentChange={{this.onContentChange}}
										@theme="dark"
									/>
								{{/if}}
							{{else}}
								{{#each this.prebuiltForm.items as |item|}}
									<CustomQuerySetupPrebuiltItemComponent
										@form={{this.prebuiltForm}}
										@model={{item}}
										@onContentChange={{this.onContentChange}}
										@isDisabled={{this.isFormDisabled}}
									/>
								{{/each}}
							{{/if}}
						</div>

						{{this.datasetHelperText}}

						<FormErrorComponent @errors={{this.form.errors}} />
					</div>

					{{#if this.showPreviewChart}}
						<div class="form__preview">

							<CustomMetricsEditActionsCustomQueryComponent
								@form={{this.form}}
								@duplicate={{this.cloneQuery}}
								@delete={{this.deleteQuery}}
								@openWizardFlow={{@toggleWizardFlow}}
								@isViewOnly={{this.isViewOnly}}
							/>

							<CustomQuerySetupCommonPreviewChartComponent @form={{this.form}} />

							{{#if this.form.availableDimensions}}
								<CustomQuerySetupCommonDimensionSelectorComponent
									@form={{this.form}}
									@theme="dark"
								/>
							{{/if}}

							{{!--
	                <div class="row edit-metric-link">
	                    <a role="button" {{on "click" this.openMetricDefinitionPopup}}>
	                        <Common::UiIcon @name="Settings" @size="sm" />
	                        Edit metric definition
	                    </a>
	                </div>
	                --}}
						</div>
					{{/if}}

				</div>

				<div class="form__bottom">

					<div
						class="name"
						{{didUpdate this.updateEditedNameFromName this.modelNotLoading.queryName}}
					>
						<FormInputComponent
							name="queryName"
							placeholder="Metric name"
							maxlength="50"
							disabled={{this.isViewOnly}}
							@value={{this.editedName}}
							@focusIn={{this.onQueryNameFocusIn}}
							@focusOut={{this.onQueryNameFocusOut}}
							@onEmpty={{setField target=this property="editedName"}}
							@theme="dark"
						/>

						{{#if this.temporaryMetric}}
							<MetricLibraryPopupsMetricBasicInfoTooltipComponent
								@model={{this.temporaryMetric}}
								@mouseLeaveDelay={{500}}
								@theme="dark"
							>
								<CommonUiIconComponent @name="InfoFill" @size="sm" />
							</MetricLibraryPopupsMetricBasicInfoTooltipComponent>
						{{/if}}
					</div>

					<div class="controls2">
						<div class="controls2__left">
							<SubmitButtonComponent
								@text="Cancel"
								@extraClass="cancel"
								@theme="grey-empty"
								@onTrigger={{this.cancel}}
							/>
						</div>
						<div class="controls2__right">
							{{#unless this.isViewOnly}}
								<SubmitButtonComponent
									@text={{this.form.previewButtonText}}
									@extraClass="button-preview"
									@theme="blue-outlined"
									@onTrigger={{this.triggerPreviewButtonAction}}
									@isDisabled={{this.form.isPreviewButtonDisabled}}
									@submitInProgress={{this.form.isPreviewButtonLoading}}
								/>

								<SubmitButtonComponent
									@text="Save"
									@extraClass="save"
									@onTrigger={{this.saveQuery}}
									@isDisabled={{this.form.isSaveButtonDisabled}}
									@submitInProgress={{this.submitInProgress}}
									@tooltipText={{this.saveQueryBtnTooltipText}}
								/>
							{{/unless}}
						</div>
					</div>

				</div>

			</div>

			{{#if this.showBottomPart}}

				{{#if this.isDatasetOrMergedDataset}}

					<CustomQuerySetupDatasetsDatasetsComponent @model={{this.datasetForm}} />

				{{else if this.form.query.isSheets}}

					{{#if this.sheetsForm.sheets}}

						{{#if this.sheetsForm.isSheetDataReloading}}
							<CustomQuerySetupSheetLoaderComponent />
						{{else}}
							<CustomQuerySetupSheetDataComponent @model={{this.sheetsForm}} />
						{{/if}}

					{{/if}}

				{{else}}

					<CustomQuerySetupCommonPreviewTableComponent @form={{this.form}} />

				{{/if}}

			{{/if}}

		{{/if}}
	</template>
}
